/**
 * FERP PDF MiniMap (DRR-079)
 *
 * Viewport overview in bottom-right corner showing current position
 * in the document. Click to navigate.
 */

import { useCallback, type MouseEvent } from 'react';
import { Box, Text, Paper } from '@mantine/core';
import { usePdfSuite } from './PdfSuiteContext';

export function MiniMap() {
  const { state, setCurrentPage } = usePdfSuite();
  const { numPages, currentPage } = state.viewer;

  const handleClick = useCallback(
    (e: MouseEvent) => {
      const el = e.currentTarget as HTMLElement;
      const rect = el.getBoundingClientRect();
      const yRatio = (e.clientY - rect.top) / rect.height;
      const targetPage = Math.max(1, Math.min(numPages, Math.ceil(yRatio * numPages)));
      setCurrentPage(targetPage);
    },
    [numPages, setCurrentPage],
  );

  if (numPages <= 1) return null;

  const viewportTop = ((currentPage - 1) / numPages) * 100;
  const viewportHeight = (1 / numPages) * 100;

  return (
    <Paper
      shadow="sm"
      withBorder
      onClick={handleClick}
      style={{
        position: 'absolute',
        bottom: 12,
        right: 12,
        width: 40,
        height: 120,
        zIndex: 20,
        cursor: 'pointer',
        backgroundColor: 'var(--mantine-color-gray-0)',
        overflow: 'hidden',
      }}
    >
      {/* Page indicators */}
      {Array.from({ length: Math.min(numPages, 20) }, (_, i) => {
        const page = Math.ceil(((i + 0.5) / Math.min(numPages, 20)) * numPages);
        return (
          <Box
            key={i}
            style={{
              position: 'absolute',
              left: 2,
              right: 2,
              top: `${(i / Math.min(numPages, 20)) * 100}%`,
              height: `${100 / Math.min(numPages, 20)}%`,
              borderBottom: '1px solid var(--mantine-color-gray-2)',
            }}
          />
        );
      })}

      {/* Viewport indicator */}
      <Box
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: `${viewportTop}%`,
          height: `${Math.max(viewportHeight, 5)}%`,
          backgroundColor: 'var(--mantine-color-blue-2)',
          border: '1px solid var(--mantine-color-blue-5)',
          opacity: 0.7,
        }}
      />

      {/* Page number */}
      <Text
        size="xs"
        ta="center"
        style={{
          position: 'absolute',
          bottom: 2,
          left: 0,
          right: 0,
          fontSize: 9,
          color: 'var(--mantine-color-dimmed)',
        }}
      >
        {currentPage}
      </Text>
    </Paper>
  );
}
